import Breadcrumbs from 'components/atoms/Breadcrumbs';
import { ERouteLinks } from 'models/route';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAlert } from 'models/alertContext';
import { useRoles } from 'pages/RolesPage/hooks';
import { initialUserFormValues, IUserFormValues } from 'models/form';
import { ActionTypes } from 'state/actions/alert';
import { EAlertVariants } from 'components/atoms/Alert';
import { EPerPages } from 'models/table';
import { StyledPageTitle } from 'theme/styles';
import TableFormWrapper from 'components/atoms/FormWrapper';
import { UserForm } from 'pages/UsersPage/UserForm';
import { getUser } from 'requests/user';
import { IUser } from 'models/user';
import { useUsers } from 'pages/UsersPage/hooks';
import { useAccount } from 'pages/AccountPage/hooks';
import { useTranslations } from 'hooks/useTranslations';
import { useDictionary } from 'models/dictionaryContext';
import { ELegalEntityTypes } from 'constants/LegalEntityTypes';
import { usePermission } from 'hooks/usePermission';
import { EPermission } from 'models/permissions';
import { ERegExpTypes } from 'constants/RegExpTypes';
import { useWeaponLicenceModule } from 'hooks/useWeaponLicenceModule';
import { handleRestApiException } from '../../../utils/handleRestApiException';

enum EEditUserPageTranslationsKeys {
  header = 'editUser.header',
  emailLabel = 'editUser.email.label',
  firstNameLabel = 'editUser.firstName.label',
  lastNameLabel = 'editUser.lastName.label',
  phonePrefixLabel = 'editUser.phonePrefix.label',
  phoneNumberLabel = 'editUser.phoneNumber.label',
  roleLabel = 'editUser.role.label',
  legalEntityPrefix = 'editUser.legalEntityPrefix.label',
  weaponLicencingAuthority = 'editUser.weaponLicencingAuthority.label',
  weaponLicencingAuthorityAll = 'editUser.weaponLicencingAuthorityAll.label',
}

const EditUserPage = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [user, setUser] = useState<IUser | null>();
  const { handleEdit } = useUsers();
  const {
    legalEntityPrefix,
    getLegalEntityPrefix,
    organisationCategory,
    getOrganisationCategory,
    weaponLicencingAuthority,
    getWeaponLicencingAuthority,
  } = useDictionary();
  const { setAlert, clearAlert } = useAlert();
  const { roles, getRolesData } = useRoles();
  const { t } = useTranslations();
  const [initialValues, setInitialValues] = useState<IUserFormValues | null>();
  const [isAssignedLELoading, setIsAssignedLELoading] = useState<boolean>(false);
  const { getAssignedLegalsToUserData, assignedLegalsToUser } = useAccount();
  const { hasPermissions } = usePermission();
  const { hasWeaponLicenceModule } = useWeaponLicenceModule();

  useEffect(() => {
    if (!legalEntityPrefix) {
      getLegalEntityPrefix();
    }
    if (!organisationCategory) {
      getOrganisationCategory();
    }
    if (!weaponLicencingAuthority) {
      getWeaponLicencingAuthority();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const compareArrayToObject = (array: string[], obj: Object): boolean => {
    const objEntries = Object.entries(obj).filter((entry) => entry[1] === true);
    return (
      objEntries.length === array.length &&
      objEntries.every((entry) => array.includes(entry[0]))
    );
  };

  useEffect(() => {
    (async function getAssignedLEsInit() {
      if (hasPermissions([EPermission.VIEW_LE]) && id) {
        setIsAssignedLELoading(true);
        await getAssignedLegalsToUserData(id);
        setIsAssignedLELoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleDeleteAssignedLegalFromUser = (LEId: string, type: ELegalEntityTypes) => {
    if (id) {
      navigate(
        `${ERouteLinks.DeleteAssignedLE.replace(':id', id).replace(
          ':LEId',
          LEId,
        )}?type=${type}`,
      );
    }
  };
  const handleAssignedLegalsToUser = () => {
    if (id) {
      navigate(ERouteLinks.AssignLESearch.replace(':id', id), {
        state: {
          assignedUniqueIds: assignedLegalsToUser.map((it) => it.uniqueId),
        },
      });
    }
  };

  const checkForChanges = (data: IUserFormValues): boolean => {
    const regexExp = new RegExp(ERegExpTypes.startsWith0);
    const normalizedUserPhoneNumber = data.phoneNumberValue.replace(regexExp, '');
    const normalizedFormPhoneNumber = initialValues?.phoneNumberValue.replace(regexExp, '');
    return (
      initialValues?.firstName === data.firstName &&
      initialValues?.lastName === data.lastName &&
      normalizedUserPhoneNumber === normalizedFormPhoneNumber &&
      initialValues?.phoneNumberCodeDictionaryUniqueId ===
      data.phoneNumberCodeDictionaryUniqueId &&
      compareArrayToObject(user!.prefixGroupIds, data.formPrefixGroupIds) &&
      compareArrayToObject(
        user!.organisationCategoryIds || [],
        data.formOrganisationCategoryIds,
      ) &&
      compareArrayToObject(user!.roleIds, data.formRoleIds) &&
      JSON.stringify(user!.licenceAuthorityIds) ===
      JSON.stringify(data.formWeaponLicencingAuthority.split(','))
    );
  };

  const handleSubmit = async (data: IUserFormValues) => {
    try {
      if (id && user) {
        if (!checkForChanges(data)) {
          const licenceAuthorityIds = hasWeaponLicenceModule()
            ? data.formWeaponLicencingAuthority.split(',')
            : [];
          await handleEdit(id, { ...data, licenceAuthorityIds });
          navigate(ERouteLinks.Users);
        } else {
          setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
            text: t('general.noChanged.warning.message'),
            variant: EAlertVariants.warning,
          });
        }
      }
    } catch (e: any) {
      setAlert(ActionTypes.SET_USER_ALERT, {
        text: handleRestApiException(e) ?? '',
        variant: EAlertVariants.error,
      });
    }
  };

  useEffect(
    () => {
      (async function getRolesDataInit() {
        await getRolesData(1, EPerPages.perPage50);
      })();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(
    () => () => {
      clearAlert(ActionTypes.CLEAR_SAME_PAGE_ALERT);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    setInitialValues((prevValues) => {
      const formInitialValues = prevValues ? { ...prevValues } : { ...initialUserFormValues };

      if (legalEntityPrefix) {
        legalEntityPrefix.forEach((prefix) => {
          if (prefix && prefix.uniqueId) {
            formInitialValues.formPrefixGroupIds[prefix.uniqueId] = false;
            if (user?.prefixGroupIds.includes(prefix.uniqueId.toString())) {
              formInitialValues.formPrefixGroupIds[prefix.uniqueId] = true;
            }
          }
        });
      }
      if (organisationCategory) {
        organisationCategory.forEach((category) => {
          if (category && category.uniqueId) {
            formInitialValues.formOrganisationCategoryIds[category.uniqueId] = false;
            if (user?.organisationCategoryIds.includes(category.uniqueId.toString())) {
              formInitialValues.formOrganisationCategoryIds[category.uniqueId] = true;
            }
          }
        });
      }
      roles.forEach((role) => {
        formInitialValues.formRoleIds[role.uniqueId] = false;
        if (user?.roleIds.includes(role.uniqueId)) {
          formInitialValues.formRoleIds[role.uniqueId] = true;
        }
      });

      return formInitialValues;
    });
  }, [roles, weaponLicencingAuthority, legalEntityPrefix, organisationCategory, user]);

  useEffect(() => {
    (async function getUserDataInit() {
      if (id) {
        const response = await getUser(id);
        if (response) {
          const formInitialValues = { ...initialUserFormValues };
          formInitialValues.email = response.email;
          formInitialValues.firstName = response.firstName;
          formInitialValues.lastName = response.lastName;
          formInitialValues.version = response.version;
          formInitialValues.phoneNumberCodeDictionaryUniqueId = response.phoneNumber
            ? response.phoneNumber.codeDictionaryUniqueId
            : '';
          formInitialValues.phoneNumberValue = response.phoneNumber
            ? response.phoneNumber.value
            : '';
          response.roleIds.forEach((role) => {
            formInitialValues.formRoleIds[role] = true;
          });
          formInitialValues.formWeaponLicencingAuthority = response.licenceAuthorityIds
            ? response.licenceAuthorityIds.toString()
            : '';
          response.prefixGroupIds.forEach((prefix) => {
            formInitialValues.formPrefixGroupIds[prefix] = true;
          });
          if (response.organisationCategoryIds) {
            response.organisationCategoryIds.forEach((categoryId) => {
              formInitialValues.formOrganisationCategoryIds[categoryId] = true;
            });
          }

          // eslint-disable-next-line no-nested-ternary
          formInitialValues.formWeaponLicencingAuthority = response.allLicenceAuthorities
            ? 'all'
            : response.licenceAuthorityIds
              ? response.licenceAuthorityIds.toString()
              : '';

          setInitialValues(formInitialValues);
          setUser(response);
        }
      }
    })();
  }, [id]);

  return (
    <>
      <Breadcrumbs
        items={[
          { label: t('module.administration.name'), route: ERouteLinks.Administration },
          {
            label: t('user.breadcrumbs.users'),
            route: ERouteLinks.Users,
          },
          t(EEditUserPageTranslationsKeys.header),
        ]}
      />
      <StyledPageTitle variant="h4">{t(EEditUserPageTranslationsKeys.header)}</StyledPageTitle>

      <TableFormWrapper>
        {!!roles.length && user && (
          <UserForm
            onCancel={() => navigate(ERouteLinks.Users)}
            onSubmit={handleSubmit}
            initialFormValues={initialValues}
            isEdit={true}
            roles={roles}
            translationsKeys={EEditUserPageTranslationsKeys}
            legalEntityPrefix={legalEntityPrefix}
            organisationCategory={organisationCategory}
            weaponLicencingAuthority={weaponLicencingAuthority}
            isAssignedLELoading={isAssignedLELoading}
            handleDeleteAssignedLegalFromUser={handleDeleteAssignedLegalFromUser}
            handleAssignedLegalsToUser={handleAssignedLegalsToUser}
            assignedLegalsToUser={
              hasPermissions([EPermission.VIEW_LE]) ? assignedLegalsToUser : []
            }
          />
        )}
      </TableFormWrapper>
    </>
  );
};

export { EditUserPage };
